import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridRowsProp,
    GridToolbar,
} from "@mui/x-data-grid";
import styled from "styled-components";
import { Employee } from "../../types";
import { SoecoButton } from "../../components/SoecoButton";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { getRolePriority } from "../../utils/util";
import { ArrowLeft, ArrowRight, Search, SearchIcon } from "lucide-react";
import { InputAdornment, Pagination, PaginationItem, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { useGetCompanies } from "../../hooks/companyHooks";

type Props = {
    employee: Employee;
    employees: Employee[];
};

export const ManageUsersList = (props: Props) => {
    const { companyId } = useParams();
    const navigate = useNavigate();
    const { data: companies } = useGetCompanies();

    const [page, setPage] = useState(1);
    const [pageSize] = useState(100);

    const handleEmployeeEdit = (id: GridRowId) => {
        navigate(`/companies/${companyId ?? ""}/manageUsers/${id}`);
    };

    const columns: GridColDef[] = [
        {
            field: "email",
            headerName: "Name",
            align: "left",
            flex: 0.9,
            editable: false,
        },
        {
            field: "role",
            headerName: "Role",
            align: "left",
            flex: 0.7,
            editable: false,
        },
        {
            field: "headCompanies",
            headerName: "Part of",
            align: "left",
            flex: 0.9,
            editable: false,
        },
        {
            field: "companyId",
            headerName: "Company",
            align: "left",
            flex: 0.9,
            editable: false,
        },

        {
            field: "edit",
            headerName: "Action",
            align: "left",
            flex: 0.3,
            editable: false,
            renderCell: (params: GridRenderCellParams<unknown, RowModel, RowModel>) => {
                return (
                    <>
                        {getRolePriority(props.employee.role) > getRolePriority(params.row.role) ? (
                            <TableActions>
                                <SoecoButton
                                    icon={true}
                                    size="small"
                                    onClick={() => handleEmployeeEdit(params.id)}
                                    disabled={true}
                                >
                                    <EditIcon fontSize="inherit" />
                                </SoecoButton>
                            </TableActions>
                        ) : (
                            <TableActions>
                                <SoecoButton
                                    icon={true}
                                    size="small"
                                    onClick={() => handleEmployeeEdit(params.id)}
                                >
                                    <EditIcon fontSize="inherit" />
                                </SoecoButton>
                            </TableActions>
                        )}
                    </>
                );
            },
        },
    ];

    type RowModel = {
        id: string;
        email: string | undefined;
        role: string;
        name: string | undefined;
        headCompanies: string;
        companyId: string | undefined;
    };

    // Names of the companies above companyId, from the top company down to its direct parent.
    const getHeadCompanies = (companyId: string | undefined): string => {
        const chain: string[] = [];
        const visited = new Set<string>();
        let current = companies?.find(c => c.id === companyId);
        while (current?.superCompany && !visited.has(current.superCompany)) {
            visited.add(current.superCompany);
            const superCompanyId: string = current.superCompany;
            current = companies?.find(c => c.id === superCompanyId);
            if (current) chain.unshift(current.name);
        }
        return chain.length > 0 ? chain.join(" › ") : "–";
    };
    const rows: GridRowsProp<RowModel> = [
        ...props.employees
            .sort((a, b) => (a.email ?? "").localeCompare(b.email ?? ""))
            .map(employee => ({
                id: employee.id,
                email: employee.email,
                role: employee.role,
                name: employee.name,
                headCompanies: getHeadCompanies(employee.companyId),
                companyId: employee.companyId
                    ? companies?.find(c => c.id === employee.companyId)?.name
                    : undefined,
            })),
    ];

    const numberOfPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };
    return (
        <EmployeeListWrapper>
            <DataGrid
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                page={page - 1}
                rowsPerPageOptions={[100]}
                hideFooter
                componentsProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: {
                            debounceMs: 500,
                            placeholder: "Search",
                            variant: "outlined",
                        },
                        sx: {
                            "& .MuiDataGrid-toolbarContainer": {
                                borderTop: "none",
                                boxShadow: "none",
                                BorderTop: "none",
                            },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "nowrap",
                            "& .MuiButton-root": {
                                backgroundColor: "white",
                                color: "#8c8c8c",
                                border: "1px solid #9e9e9e",
                                marginLeft: "20px",
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: "600",
                                height: "42px",
                                minHeight: "42px",
                                padding: "0 8px",
                                fontSize: "14px",
                            },
                            "& .MuiTextField-root": {
                                color: "red",
                                borderRadius: "20px",
                                placeholder: "OK",

                                "& .MuiInputBase-root": {
                                    borderRadius: "50px",
                                    color: "black",
                                    border: "1px solid #9e9e9e",
                                    marginRight: "20px",
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                    fontWeight: "400",
                                    height: "42px",
                                    minHeight: "42px",
                                    padding: "0 8px",
                                    fontSize: "14px",
                                },
                            },
                        },
                    },
                    pagination: {
                        count: numberOfPages,
                    },
                }}
            />

            {/* Customized pagination footer */}
            <Stack
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                marginTop={2}
            >
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={page}
                    onChange={handlePageChange}
                    count={numberOfPages}
                    renderItem={item => (
                        <PaginationItem
                            {...item}
                            components={{
                                previous: () => (
                                    <ArrowLeft style={{ cursor: "pointer" }} size={16} />
                                ),
                                next: () => <ArrowRight style={{ cursor: "pointer" }} size={16} />,
                            }}
                            sx={{
                                backgroundColor: item.selected ? "black" : "transparent",
                                color: item.selected ? "white" : "black",
                                "&:hover": {
                                    backgroundColor: item.selected ? "black" : "lightgrey",
                                },
                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                            }}
                        />
                    )}
                />
            </Stack>
        </EmployeeListWrapper>
    );
};

const EmployeeListWrapper = styled.div`
    width: 100%;
    height: 100%;
    max-height: 100%;
`;

const TableActions = styled.div`
    display: flex;
    font-size: 6px;
`;
